/**
 * Histórico MENSUAL del Mercado Agroganadero de Cañuelas para /historico-precios.
 *
 * El build (scripts/build-mag-historico.mjs) baja los subtotales diarios por
 * categoría de precios_mag_diarios (subcategoria = '') y los agrupa por mes con
 * `agruparMesesMag`. El promedio del mes se pondera por los kilos vendidos cada
 * día, igual que el subtotal diario del mercado; los días sin kilos informados
 * solo cuentan si en el mes no hay ninguno con kilos.
 *
 * Fuente distinta de los remates (cattle_prices): hacienda de FAENA, no de
 * invernada. Nunca se mezclan en la misma serie.
 */

export const CATEGORIAS_MAG = ['novillos', 'novillitos', 'vaquillonas', 'vacas', 'toros', 'mej'] as const;
export type CategoriaMag = (typeof CATEGORIAS_MAG)[number];

export interface FilaSubtotalMag {
  fecha: string; // YYYY-MM-DD
  categoria: string; // NOVILLOS, NOVILLITOS, ... (como en la base)
  promedio: number | string;
  kgs?: number | string | null;
  cabezas?: number | string | null;
}

export type MesMag = {
  year: number;
  month: number;
  label: string; // "Abr 19"
  /** Cabezas vendidas en el mes, todas las categorías. */
  cabezas: number;
} & Record<CategoriaMag, number | null>;

export interface MagHistorico {
  generatedAt: string;
  meses: MesMag[];
}

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const aNumero = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function agruparMesesMag(filas: FilaSubtotalMag[]): MesMag[] {
  type Acum = { conKg: number; kg: number; sinKg: number; nSinKg: number };
  const porMes = new Map<string, { year: number; month: number; cabezas: number; cats: Map<CategoriaMag, Acum> }>();

  for (const f of filas) {
    const cat = String(f.categoria).toLowerCase() as CategoriaMag;
    const promedio = aNumero(f.promedio);
    if (!CATEGORIAS_MAG.includes(cat) || promedio == null || promedio <= 0) continue;
    const year = Number(f.fecha.slice(0, 4));
    const month = Number(f.fecha.slice(5, 7));
    if (!year || !month) continue;

    const clave = `${year}-${month}`;
    let mes = porMes.get(clave);
    if (!mes) {
      mes = { year, month, cabezas: 0, cats: new Map() };
      porMes.set(clave, mes);
    }
    mes.cabezas += aNumero(f.cabezas) ?? 0;

    let a = mes.cats.get(cat);
    if (!a) {
      a = { conKg: 0, kg: 0, sinKg: 0, nSinKg: 0 };
      mes.cats.set(cat, a);
    }
    const kgs = aNumero(f.kgs);
    if (kgs != null && kgs > 0) {
      a.conKg += promedio * kgs;
      a.kg += kgs;
    } else {
      a.sinKg += promedio;
      a.nSinKg += 1;
    }
  }

  return Array.from(porMes.values())
    .sort((x, y) => x.year - y.year || x.month - y.month)
    .map((m) => {
      const fila = {
        year: m.year,
        month: m.month,
        label: `${MESES[m.month - 1]} ${String(m.year).slice(2)}`,
        cabezas: m.cabezas,
      } as MesMag;
      for (const cat of CATEGORIAS_MAG) {
        const a = m.cats.get(cat);
        fila[cat] = !a ? null : a.kg > 0 ? a.conKg / a.kg : a.nSinKg > 0 ? a.sinKg / a.nSinKg : null;
      }
      return fila;
    });
}

/**
 * Dólar e índice de cada mes para convertir la serie de Cañuelas, tomados de
 * la serie de los remates (prices-snapshot). Los meses posteriores al último de
 * esa serie (el mes en curso) usan el dólar y el índice de hoy; un mes faltante
 * en el medio queda en null (mejor un hueco en el gráfico que un valor en pesos
 * mostrado como si fuera dólares).
 */
export function indicesDelMes(
  historia: { year: number; month: number; usd: number | null; uva: number | null }[],
  year: number,
  month: number,
  usdHoy: number | null,
  uvaHoy: number | null
): { usd: number | null; uva: number | null } {
  const p = historia.find((h) => h.year === year && h.month === month);
  if (p) return { usd: p.usd, uva: p.uva };
  const ultimo = historia[historia.length - 1];
  const posterior = !ultimo || year * 12 + month > ultimo.year * 12 + ultimo.month;
  return posterior ? { usd: usdHoy, uva: uvaHoy } : { usd: null, uva: null };
}
